import { Dropdown, Avatar, Badge, Typography, Space } from 'antd';
import { SwapOutlined, PlusOutlined, SettingOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useOrganizationStore from '../../store/organizationStore';
import type { MenuProps } from 'antd';

const { Text } = Typography;

/**
 * OrganizationSwitcher Component
 *
 * Displays current organization and allows switching between organizations
 * Appears in the Header component between breadcrumbs and user menu
 *
 * Features (from MULTI_TENANT_ARCHITECTURE.md):
 * - Shows current organization name and code
 * - Dropdown showing all user's organizations
 * - Role badge for each organization (OWNER, ADMIN, etc.)
 * - Visual indicator for current organization
 * - Quick switch functionality
 * - "Add Organization" option
 * - "Manage Organizations" option
 */
export default function OrganizationSwitcher() {
  const navigate = useNavigate();
  const store = useOrganizationStore();
  const { currentOrganization, switchOrganization } = store;

  // Ensure userOrganizations is ALWAYS an array - defensive programming
  const userOrganizations = Array.isArray(store.userOrganizations) ? store.userOrganizations : [];

  /**
   * Get badge color based on user's role in organization
   */
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'OWNER':
        return '#faad14'; // gold
      case 'ADMIN':
        return '#1890ff'; // blue
      case 'ACCOUNTANT':
        return '#52c41a'; // green
      case 'USER':
        return '#8c8c8c'; // gray
      case 'VIEWER':
        return '#d9d9d9'; // light gray
      default:
        return '#8c8c8c';
    }
  };

  /**
   * Handle organization switch
   */
  const handleSwitch = async (organizationId: string) => {
    if (organizationId === currentOrganization?.id) {
      return; // Already selected
    }

    try {
      await switchOrganization(organizationId);
      // switchOrganization will reload the page
    } catch (error) {
      console.error('Failed to switch organization:', error);
    }
  };

  /**
   * Build dropdown menu items
   */
  const menuItems: MenuProps['items'] = [
    // Organization list
    ...userOrganizations.map((userOrg) => ({
      key: userOrg.organization.id,
      label: (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text strong style={{ fontSize: 14 }}>
              {userOrg.organization.name}
            </Text>
            {userOrg.organization.id === currentOrganization?.id && (
              <CheckOutlined style={{ color: '#1890ff', fontSize: 14 }} />
            )}
          </div>
          <Space size={8}>
            {userOrg.organization.code && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {userOrg.organization.code}
              </Text>
            )}
            <Badge
              count={userOrg.role}
              style={{
                backgroundColor: getRoleBadgeColor(userOrg.role),
                fontSize: 10,
                padding: '0 6px',
                height: 18,
                lineHeight: '18px',
              }}
            />
          </Space>
        </Space>
      ),
      icon:
        userOrg.organization.id === currentOrganization?.id ? (
          <SwapOutlined style={{ color: '#1890ff' }} />
        ) : (
          <SwapOutlined style={{ color: '#8c8c8c' }} />
        ),
      onClick: () => handleSwitch(userOrg.organization.id),
      style: {
        padding: '12px 16px',
        background: userOrg.organization.id === currentOrganization?.id ? '#f0f7ff' : 'transparent',
      },
    })),

    // Divider
    {
      type: 'divider',
    },

    // Add Organization
    {
      key: 'add',
      label: 'Add Organization',
      icon: <PlusOutlined />,
      onClick: () => navigate('/organizations/new'),
    },

    // Manage Organizations
    {
      key: 'manage',
      label: 'Manage Organizations',
      icon: <SettingOutlined />,
      onClick: () => navigate('/organizations'),
    },
  ];

  // Don't show if no current organization
  if (!currentOrganization) {
    return null;
  }

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomRight"
      overlayStyle={{ minWidth: 280 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 12px',
          borderRadius: 6,
          cursor: 'pointer',
          transition: 'background 0.2s',
          border: '1px solid transparent',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
          e.currentTarget.style.borderColor = '#d9d9d9';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
        }}
      >
        {/* Organization Avatar */}
        <Avatar
          size="small"
          src={currentOrganization.logo_url}
          style={{
            backgroundColor: '#1890ff',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {currentOrganization.name.charAt(0).toUpperCase()}
        </Avatar>

        {/* Organization Info */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
          <Text
            strong
            style={{
              fontSize: 13,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 150,
            }}
          >
            {currentOrganization.name}
          </Text>
          <Text
            type="secondary"
            style={{
              fontSize: 11,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {currentOrganization.code || 'ORG'}
          </Text>
        </div>

        {/* Dropdown Icon */}
        <SwapOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
      </div>
    </Dropdown>
  );
}
